import React, { useMemo } from 'react';
import { Box, Button, Card, FormControl, FormLabel, Input, Option, Select, Stack, Typography, Sheet } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { DocumentWizardSharedProps } from '../components/DocumentWizardShared';
import { MaterialsCard, LabourCard, EquipmentCard, AdditionalCostsCard, DurationCard } from '../components/DocumentWizardCards';
import { DepositCard, TotalsCard } from '../components/DocumentWizardTotals';
import { computeTotals } from '../types/documents.types';
import DescriptionAssistant from '../components/DescriptionAssistant';
import { TITLE_TEMPLATES } from '../data/titleTemplates';
import QuoteVisualDesignerCard from './QuoteVisualDesignerCard';
import { VisualDesignerPairingCard, type VisualDesignerPairingModel } from './VisualDesignerPairingCard';

interface CreateWizardProps extends DocumentWizardSharedProps {
  handleCreate: () => Promise<void | boolean>;
  onOpenMaterial: () => void;
  onOpenEquipment: () => void;
  onOpenLabour: () => void;
  onOpenHelp: () => void;
  onCancel?: () => void;
  omitVisualDesigner?: boolean;
  omitActionButtons?: boolean;
  visualPairing?: VisualDesignerPairingModel;
}

export const CreateWizard: React.FC<CreateWizardProps> = ({
  kind,
  lifecycleRef,
  title,
  setTitle,
  description,
  setDescription,
  selectedCustomer,
  lines,
  setLines,
  materials,
  equipment,
  labourRates,
  orgs,
  organisationId,
  displayDays,
  setDisplayDays,
  displayHours,
  setDisplayHours,
  depositEnabled,
  setDepositEnabled,
  depositMode,
  setDepositMode,
  depositPct,
  setDepositPct,
  depositFixed,
  setDepositFixed,
  depositAmount,
  paymentStructureApplicable,
  setPaymentStructureApplicable,
  paymentStructurePattern,
  setPaymentStructurePattern,
  fees,
  setFees,
  vatEnabled,
  setVatEnabled,
  vatPct,
  setVatPct,
  handleCreate,
  loading,
  onOpenMaterial,
  onOpenEquipment,
  onOpenLabour,
  onOpenHelp,
  onCancel,
  omitVisualDesigner = false,
  omitActionButtons = false,
  visualPairing,
}) => {
  const navigate = useNavigate();

  const setTitleNormalized = (next: string) => {
    const s = String(next ?? '');
    if (kind === 'Estimate' || kind === 'Quotation') {
      if (!s) return setTitle(s);
      return setTitle(s.charAt(0).toLowerCase() + s.slice(1));
    }
    return setTitle(s);
  };
  
  const totals = useMemo(() => {
    const result = computeTotals(lines, fees, { enabled: vatEnabled, pct: vatPct });
    return result;
  }, [lines, fees, vatEnabled, vatPct]);

  const defaultVisualDesignerReference = useMemo(() => {
    const name = String(selectedCustomer?.name || '').trim();
    if (!name) return '';
    const parts = name.split(/\s+/).filter(Boolean);
    const surname = (parts.slice(1).join(' ') || parts[0] || '').trim();
    return surname.toUpperCase();
  }, [selectedCustomer?.name]);

  return (
    <>
      {/* Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2, gap: 2 }}>
        <Typography level="h3">New {kind === 'Estimate' ? 'Estimate' : kind === 'Quotation' ? 'Quotation' : 'Invoice'}{lifecycleRef ? ` - ${lifecycleRef}` : ''}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button size="sm" variant="outlined" onClick={onOpenMaterial}>Manage Materials</Button>
          <Button size="sm" variant="outlined" onClick={onOpenEquipment}>Manage Equipment</Button>
          <Button size="sm" variant="outlined" onClick={onOpenLabour}>Manage Labour Rates</Button>
        </Stack>
      </Stack>

      {/* Details */}
      <Card sx={{ p: 2, mb: 2 }}>
        {visualPairing && kind !== 'Invoice' && (
          <VisualDesignerPairingCard
            pairing={visualPairing}
            documentNoun={kind === 'Estimate' ? 'estimate' : 'quote'}
          />
        )}
        <Typography level="title-md" sx={{ mb: 1 }}>Details</Typography>
        <Stack spacing={1}>
          <FormControl>
            <FormLabel>Title</FormLabel>
            <Input value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitleNormalized(e.target.value)} />
          </FormControl>
          {/* Title Templates */}
          <Sheet variant="soft" sx={{ p: 1, borderRadius: 6 }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>Title Templates</Typography>
            <Stack spacing={1}>
              {TITLE_TEMPLATES.filter(t => {
                const code = orgs.find(o => o.id === organisationId)?.code;
                return !t.org || t.org === code;
              }).map((t, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center">
                  <Typography level="body-sm" sx={{ flex: 1 }}>{t.text}</Typography>
                  <Button size="sm" onClick={() => setTitleNormalized(t.text)}>Use</Button>
                </Stack>
              ))}
            </Stack>
          </Sheet>
          <DescriptionAssistant
            orgName={orgs.find(o => o.id === organisationId)?.name}
            orgCode={orgs.find(o => o.id === organisationId)?.code}
            reference={lifecycleRef}
            title={title}
            value={description}
            lines={lines}
            onChange={setDescription}
          />
        </Stack>
      </Card>

      {/* Line Items */}
      <MaterialsCard lines={lines} setLines={setLines} materials={materials} />
      <LabourCard lines={lines} setLines={setLines} labourRates={labourRates} />
      <EquipmentCard lines={lines} setLines={setLines} equipment={equipment} />
      <AdditionalCostsCard lines={lines} setLines={setLines} />

      {/* Duration */}
      <DurationCard 
        displayDays={displayDays} 
        setDisplayDays={setDisplayDays} 
        displayHours={displayHours} 
        setDisplayHours={setDisplayHours} 
      />

      {/* Deposit */}
      <DepositCard 
        depositEnabled={depositEnabled}
        setDepositEnabled={setDepositEnabled}
        depositMode={depositMode}
        setDepositMode={setDepositMode}
        depositPct={depositPct}
        setDepositPct={setDepositPct}
        depositFixed={depositFixed}
        setDepositFixed={setDepositFixed}
        depositAmount={depositAmount}
        paymentStructureApplicable={paymentStructureApplicable}
        setPaymentStructureApplicable={setPaymentStructureApplicable}
        paymentStructurePattern={paymentStructurePattern}
        setPaymentStructurePattern={setPaymentStructurePattern}
        grandTotal={totals.grandTotal}
      />

      {/* Totals */}
      <TotalsCard 
        fees={fees}
        setFees={setFees}
        vatEnabled={vatEnabled}
        setVatEnabled={setVatEnabled}
        vatPct={vatPct}
        setVatPct={setVatPct}
        displayDays={displayDays}
        displayHours={displayHours}
        totals={totals}
      />

      {/* Visual Designer sits below commercial details (standalone /docs create). */}
      {!omitVisualDesigner && kind !== 'Invoice' && (
        <QuoteVisualDesignerCard
          lines={lines}
          setLines={setLines}
          defaultReference={defaultVisualDesignerReference}
          documentId={null}
          customerId={selectedCustomer?.id ?? null}
          currentVersionId={null}
          availableVersions={[]}
        />
      )}

      {/* Action Buttons */}
      {!omitActionButtons && (
        <Card sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2, justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="soft" onClick={onOpenHelp}>Help?</Button>
            <Button 
              variant="outlined" 
              onClick={() => (onCancel ? onCancel() : navigate('/docs'))}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} loading={loading}>Create</Button>
          </Stack>
        </Card>
      )}
    </>
  );
};
